/**
 * Source mapping utilities for Purchase Requests and Materials
 * Maps API Key short names to their corresponding long names
 */

import {getStringValueForSourceFromApiKey} from './constants';

/**
 * Source mapping from short name (API Key value) to long name (display label)
 */
export const SOURCE_MAPPINGS = {
    'PNC': 'PUNCHOUT CATALOG',
    'CDX': 'CANDEX CATALOG',
    'NCT': 'NON CATALOG',
    'NSC': 'NESTLE SUPPLIER CATALOG',
    'AGM': 'AGENCY MANIA',
    'TSF': 'TECHNICAL SOURCING FORM',
    'OMT': 'ORDER MANAGEMENT TOOL',
    'FMK': 'FAIRMARKIT',
    'SNW': 'SERVICENOW',
    'PSL': 'PREFERRED SUPPLIER LIST',
    'NBC': 'NESTBUY CATALOG',
};

/**
 * Special value used when multiple different sources are present in a PR
 */
export const MIX_SOURCE_VALUE = 'MIX';

/**
 * Convert a source short name to a LabelValue object
 * @param {string} sourceShortName - The short name from API Key (e.g., 'PNC', 'CDX')
 * @return {LabelValue} The LabelValue object with label and value
 */
export const convertSourceToLabelValue = (sourceShortName) => {
    if (!sourceShortName) {
        return {label: '', value: ''};
    }

    const normalizedSource = sourceShortName.toUpperCase().trim();
    const longName = SOURCE_MAPPINGS[normalizedSource];

    if (!longName) {
        // If source not in mapping, use the source itself as both label and value
        return {
            label: normalizedSource,
            value: normalizedSource,
        };
    }

    return {
        label: `${normalizedSource} - ${longName}`,
        value: normalizedSource,
    };
};

/**
 * Determine the PR source based on material sources
 * If all materials have the same source, use that source
 * If materials have different sources, use MIX with concatenated labels
 * @param {Array<LabelValue>} materialSources - Array of material sources
 * @return {LabelValue} The PR source
 */
export const determinePRSource = (materialSources) => {
    if (!materialSources || materialSources.length === 0) {
        return {label: '', value: ''};
    }

    // Filter out empty sources
    const validSources = materialSources.filter((source) => source && source.value);

    if (validSources.length === 0) {
        return {label: '', value: ''};
    }

    // Check if all sources are the same
    const firstSourceValue = validSources[0].value;
    const allSame = validSources.every((source) => source.value === firstSourceValue);

    if (allSame) {
        // All materials have the same source
        return validSources[0];
    } else {
        // Materials have different sources - create MIX
        // Get unique labels
        const uniqueValues = [...new Set(validSources.map((source) => source.value))];
        const concatenatedLabel = uniqueValues.join(' - ');

        return {
            label: concatenatedLabel,
            value: MIX_SOURCE_VALUE,
        };
    }
};

/**
 * Get source from API Key or from provided source field
 * Priority: provided source field > API Key source
 * @param {string|undefined} providedSource - Source provided in the API request
 * @param {string} apiKeySource - Source from the API Key
 * @return {LabelValue} The material source as LabelValue
 */
export const getMaterialSource = (providedSource, apiKeySource) => {
    // If source is provided in the request, use it; otherwise use API Key source
    const sourceToUse = providedSource || apiKeySource;
    // Special case for Promo source - return NSC instead of PROMO
    if (sourceToUse?.toUpperCase() === 'PROMO') {
        return convertSourceToLabelValue('NSC');
    }
    return convertSourceToLabelValue(sourceToUse);
};

/**
 * Mapping from old display values to new short codes
 */
const OLD_DISPLAY_TO_NEW_CODE = {
    'PunchOut Catalog': 'PNC',
    'Sourcing Event (Fairmarkit)': 'FMK',
    'NSC': 'NSC',
    'PSL': 'PSL',
    'Non-PSL (Describe Your Need)': 'NCT',
    'Sourcing Event (Agency Mania)': 'AGM',
    'Sourcing Event (TSF)': 'TSF',
    'Sourcing Event (Promo)': 'NSC',
    'Candex': 'CDX',
    'Nestlé Supplier Catalog': 'NSC',
};

/**
 * Convert old PR source fields to new LabelValue format
 * This function is used for migration of existing PRs
 * @param {string} source - The old "source" field
 * @param {string} sourceFromApiKey - The old "sourceFromApiKey" field
 * @param {string} sourceToDisplay - The old "sourceToDisplay" field
 * @return {LabelValue} The PR source in new format
 */
export const convertOldPRSourceToLabelValue = (source, sourceFromApiKey, sourceToDisplay) => {
    let displayValue = '';

    // Priority 1: Use sourceToDisplay if available
    if (sourceToDisplay) {
        displayValue = sourceToDisplay;
    } else if (getStringValueForSourceFromApiKey) {
        // Priority 2: Use the function to get display value from source and sourceFromApiKey
        displayValue = getStringValueForSourceFromApiKey(source, sourceFromApiKey);
    } else {
        // Fallback: use source field
        displayValue = source;
    }

    if (!displayValue) {
        return {label: '', value: ''};
    }

    // Check if we have a direct mapping
    const mappedCode = OLD_DISPLAY_TO_NEW_CODE[displayValue];
    if (mappedCode) {
        return convertSourceToLabelValue(mappedCode);
    }

    // Handle "PSL / Non-PSL" or any mixed sources
    if (displayValue.includes('/')) {
        const parts = displayValue.split('/').map((p) => p.trim());
        const mappedParts = parts
            .map((part) => OLD_DISPLAY_TO_NEW_CODE[part] || part)
            .filter(Boolean);

        if (mappedParts.length > 1) {
            // Multiple sources - create MIX
            const values = mappedParts.map((code) => {
                const labelValue = convertSourceToLabelValue(code);
                return labelValue.value || code;
            });
            return {
                label: values.join(' - '),
                value: MIX_SOURCE_VALUE,
            };
        } else if (mappedParts.length === 1) {
            return convertSourceToLabelValue(mappedParts[0]);
        }
    }

    // Handle "Sourcing Event (XXX)" format
    const sourcingEventMatch = displayValue.match(/Sourcing Event \((.+)\)/);
    if (sourcingEventMatch) {
        const sourcingEventName = sourcingEventMatch[1];
        // Try to map the sourcing event name to a known source
        const possibleMappings = {
            'Agency Mania': 'AGM',
            'TSF': 'TSF',
            'Fairmarkit': 'FMK',
        };
        const mappedSource = possibleMappings[sourcingEventName];
        if (mappedSource) {
            return convertSourceToLabelValue(mappedSource);
        }
    }

    // Try to match directly with our SOURCE_MAPPINGS values (case insensitive)
    const displayValueUpper = displayValue.toUpperCase();
    for (const [code, label] of Object.entries(SOURCE_MAPPINGS)) {
        if (label === displayValueUpper || displayValue.toUpperCase() === code) {
            return convertSourceToLabelValue(code);
        }
    }

    // If nothing matches, return the display value as both label and value
    return {
        label: displayValue,
        value: displayValue.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3),
    };
};

